import React, { useContext, useState } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames/bind';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { Button, Col, Image, Overlay } from '@thd-olt-component-react/core-ui';
import { Tooltip } from '@thd-olt-component-react/tooltip';

import {
  params,
  alias,
  shape as shapeType,
  string as stringType,
  bool as boolType,
} from '@thd-nucleus/data-sources';
import { useDataModel } from '@thd-nucleus/data-sources/react/dataModel/useDataModel';
import styles from './online-door.module.scss';

let cx = classNames.bind(styles);

const isVirtualConsultingAvailable = (data) => data?.serviceAddOns?.consultationDetails?.virtualAssistanceFlag;

export const OnlineDoor = ({ itemId }) => {
  const [open, setOpen] = useState(false);
  const { channel } = useContext(ExperienceContext);

  const { storeId } = useStore();

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: { itemId },
    ssr: false,
    skip: storeId === '8119',
  });

  const buttonURL = '/services/i/doorswindows-dp-tv/b66b6826d/form?flow=the_home_depot_product_email';

  const displayModal = () => {
    setOpen(true);
  };

  const { data: serviceAddOnsData } = useDataModel('serviceAddOns', {
    variables: { itemId, storeId },
    ssr: false,
    skip: !buttonURL || storeId === '8119',
  });

  if (!buttonURL || !isVirtualConsultingAvailable(serviceAddOnsData)) {
    return null;
  }

  const contactDetails = {
    contactNumber: '18334327766',
    contactLabel: '1-833-HD-APRON (1-833-432-7766)',
  };

  const HoverContent = (
    <div className={cx('tooltip')}>
      <div className={cx('tooltip__ul')}>
        <p className={cx('tooltip__title')}>HOW IT WORKS</p>
        <p>What can we help you with?</p>
        <ul className={cx('tooltip__list', 'tooltip__sublist')}>
          <li>Available styles</li>
          <li>How to measure</li>
          <li>Custom door/window designs</li>
          <li>Pro orders</li>
          <li>Installation</li>
        </ul>
      </div>
      <ol className={cx('tooltip__list', 'tooltip__ol')}>
        <li>
          <p className={cx('tooltip__title')}>Request a free virtual appointment</p>
          <p>
            Request a free virtual appointment to schedule a time to speak with a door and window
            associate or call 1-833-HDAPRON (432-7766) from 9 a.m. to 8 p.m. EST, 7 days/week.
          </p>
        </li>
        <li>
          <p className={cx('tooltip__title')}>Speak with an associate</p>
          <p>
            How can we help? Whether you have questions about product availability or features or
            need a quote for a custom door or window solution, we&apos;ve got you covered.
          </p>
        </li>
        <li>
          <p className={cx('tooltip__title')}>We&apos;ll follow up</p>
          <p>We&apos;ll make sure you have everything you need for your project!</p>
        </li>
      </ol>
    </div>
  );

  return (
    <div className={cx('online-door')}>
      <Col nopadding fallback={12} sm={12} className={cx('online-door__banner-container')}>
        <div className={cx('online-door__man')}>
          <div className={cx('online-door__left-main')}>
            <div className={cx('online-door__left')}>
              <Image
                className={cx('online-door__banner-image1')}
                src="http://assets.homedepot-static.com/images/v1/mvaman.png"
                alt="mva man icon"
                title="Image Title"
                lazy={false}
              />
              <div className={cx('online-door__banner-text')}>
                <div className={cx('online-door__banner-text-main')}>
                  <span className={cx('online-door__banner-text-small')}>WE&rsquo;RE HERE</span>
                  <span className={cx('online-door__banner-text-large')}>TO HELP</span>
                </div>
              </div>
            </div>
          </div>

          <div fallback={12} sm={4} className={cx('online-door__info-container')}>
            <div className={cx('online-door__info-content')}>
              <div className={cx('online-door__info')}>
                <div className={cx('online-door__info-text')}>
                  <span className={cx('online-door__info-text__primary')}>
                    Speak to a Virtual Associate today
                  </span>
                  <span className={cx('online-door__info-text__secondary')}>
                    about Doors or Windows
                  </span>
                </div>
              </div>
              <Button
                className={cx('online-door__info__button')}
                outline
                small
                orange="true"
                target="_blank"
                rel="noopener noreferrer"
                onClick={displayModal}
              >
                Request Appointment
              </Button>
              <div className={cx('online-door__phone-text')}>
                <a
                  className={cx('u__default-link', 'online-door__phone-number')}
                  href={`tel:${contactDetails.contactNumber}`}
                >
                  <span className={cx('online-door__phone-text__bold')}>Or CALL</span>
                  {contactDetails.contactLabel}
                </a>
                <span className={cx('online-door__phone-text__light')}>Monday-Sunday 9AM-8PM EST</span>
              </div>

              <Tooltip channel={channel} placement="top" content={HoverContent} closeButton>
                <div className={cx('online-door__tooltip-wrapper')}>
                  <img
                    height="100%"
                    width="100%"
                    className={cx('online-door__icon-info')}
                    src="https://assets.homedepot-static.com/images/v1/info.svg"
                    alt=""
                    aria-hidden="true"
                  />
                  <span className={cx('online-door__tooltip-info')}>What to Expect</span>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      </Col>
      <Overlay onClose={() => setOpen(false)} open={open} medium closeButton>
        <iframe title="Request Appointment" src={buttonURL} width="100%" height="99%" />
      </Overlay>
    </div>
  );
};

OnlineDoor.propTypes = {
  itemId: string.isRequired,
};

OnlineDoor.dataModel = {
  clientOnlyProduct: alias('product')
    .params({ itemId: stringType().isRequired() })
    .shape({
      itemId: stringType(),
      dataSources: stringType(),
      info: shapeType({
        consultationType: stringType(),
      }),
    }),
  serviceAddOns: params({
    itemId: stringType().isRequired(),
    storeId: stringType().isRequired(),
  }).shape({
    consultationDetails: shapeType({
      virtualAssistanceFlag: boolType(),
    }),
  }),
};